import { useEffect, useRef, useState } from "react";
import { zodiacSymbol } from "../data/models";
import { useServices } from "../data/services";
import { useAsync } from "../data/useAsync";
import { absoluteDay, count, money, relativeShort } from "../data/format";
import {
  addToHomeScreen,
  cloud,
  haptic,
  isHosted,
  openTelegram,
  shareToStory,
  showAlert,
} from "../platform/telegram";
import { useMainButton } from "../platform/hooks";
import { useNav } from "../nav/router";
import { Screen } from "../nav/Shell";
import { Avatar, Button, Card, CourseBadge, HandoffButton, Pill } from "../components/primitives";
import { EmptyState, ErrorState, FeedSkeleton, Skeleton } from "../components/states";
import "./profile.css";

/**
 * Profile — the hub and its five pushed screens, per "07 Profile".
 *
 * Reward language is perks and points only — never currency, tokens, or a
 * wallet. Academic data (GPA, grades) never appears on the public profile.
 */

export function ProfileTab() {
  const { auth, reward } = useServices();
  const { push } = useNav();
  const user = useAsync(() => auth.getCurrentUser(), []);
  const balance = useAsync(() => reward.getBalance(), []);

  return (
    <Screen>
      <div className="profile-head">
        <Avatar
          id={user.data?.id ?? "me"}
          name={user.data?.displayName ?? ""}
          photoURL={user.data?.profilePhotoURL}
          size={56}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 className="t-title">
            {user.data?.displayName ?? <Skeleton height={22} width={140} />}
          </h1>
          <div className="course-row__meta">
            {user.data ? `${user.data.school} '29 · ${user.data.major}` : ""}
          </div>
        </div>
        <button
          type="button"
          className="btn btn--quick"
          onClick={() => {
            haptic.tap();
            push({ name: "editProfile" });
          }}
        >
          Edit
        </button>
      </div>

      {/* Perks card — brand tint, same D2 surface rule as the GPA hero. */}
      <div className="gpa-hero" style={{ margin: "var(--space-2) 0 var(--space-4)" }}>
        <div className="t-micro" style={{ color: "var(--brand-tint-text)" }}>
          Campus perks
        </div>
        <div className="gpa-hero__value">
          {balance.data ? `${count(balance.data.balance)} pts` : "—"}
        </div>
        <button
          type="button"
          className="link"
          style={{ fontSize: "var(--text-caption-size)", background: "none", padding: 0 }}
          onClick={() => {
            haptic.tap();
            push({ name: "marketplace" });
          }}
        >
          Marketplace{" "}
          <i className="fa-solid fa-chevron-right" style={{ fontSize: 10 }} aria-hidden="true" />
        </button>
      </div>

      <div className="stack">
        <HubRow
          icon="fa-clock-rotate-left"
          label="Rewards history"
          onOpen={() => push({ name: "rewards" })}
        />
        <HubRow
          icon="fa-id-card"
          label="Public profile preview"
          onOpen={() => push({ name: "publicProfile", userId: "user-001" })}
        />
        <HubRow
          icon="fa-moon"
          label={`Daily horoscope · ${user.data?.zodiacSign ?? ""}`}
          chip="New"
          onOpen={() => push({ name: "horoscope" })}
        />
        <HubRow
          icon="fa-gear"
          label="Settings & privacy"
          onOpen={() => push({ name: "settings" })}
        />
      </div>

      <div style={{ marginTop: "var(--space-4)" }}>
        <HandoffButton
          block
          label="Share my profile to a chat"
          onOpen={() => openTelegram("https://t.me/share/url?url=")}
        />
      </div>
    </Screen>
  );
}

function HubRow({
  icon,
  label,
  chip,
  onOpen,
}: {
  icon: string;
  label: string;
  chip?: string;
  onOpen: () => void;
}) {
  return (
    <Card compact onClick={onOpen}>
      <div className="hub-row">
        <i className={`fa-solid ${icon} hub-row__icon`} aria-hidden="true" />
        <span className="hub-row__label">{label}</span>
        {chip ? <Pill tone="state">{chip}</Pill> : null}
        <i className="fa-solid fa-chevron-right course-row__chevron" aria-hidden="true" />
      </div>
    </Card>
  );
}

/* ---------------- Public profile (pushed; any user) ---------------- */

export function PublicProfile({ userId }: { userId: string }) {
  const { feed, matching, academic } = useServices();
  const isSelf = userId === "user-001";

  const user = useAsync(() => feed.getUser(userId), [userId]);
  const vibe = useAsync(() => matching.getVibeProfile(isSelf ? undefined : userId), [userId]);
  const courses = useAsync(
    async () =>
      isSelf
        ? (await academic.getCourses()).map((c) => c.code ?? c.name)
        : feed.getSharedCourses(userId),
    [userId],
  );

  if (user.status === "error") {
    return (
      <Screen>
        <ErrorState onRetry={user.reload} />
      </Screen>
    );
  }

  return (
    <Screen>
      <div className="profile-head">
        <Avatar
          id={userId}
          name={user.data?.displayName ?? ""}
          photoURL={user.data?.profilePhotoURL}
          size={56}
        />
        <div>
          <h1 className="t-title">
            {user.data?.displayName ?? <Skeleton height={22} width={140} />}
          </h1>
          <div className="course-row__meta">
            {user.data ? `${user.data.school} '29 · ${user.data.major}` : ""}
          </div>
        </div>
      </div>

      {vibe.data?.interests && vibe.data.interests.length > 0 ? (
        <div className="card__row" style={{ marginBottom: "var(--space-3)" }}>
          {vibe.data.interests.slice(0, 5).map((tag) => (
            <Pill key={tag} tone="state">
              {tag}
            </Pill>
          ))}
        </div>
      ) : null}

      {user.data?.vibeAnalysisText ? (
        <Card compact>
          <div className="card__body">
            <div className="card__content profile-bio">“{user.data.vibeAnalysisText}”</div>
          </div>
        </Card>
      ) : null}

      {(courses.data ?? []).length > 0 ? (
        <div className="card__row" style={{ margin: "var(--space-3) 0" }}>
          {(courses.data ?? []).map((code) => (
            <CourseBadge key={code} code={code} />
          ))}
        </div>
      ) : null}

      <div className="well-empty" style={{ marginTop: "var(--space-2)" }}>
        What others see. GPA, grades, and rewards are never public.
      </div>

      {!isSelf ? (
        <div style={{ marginTop: "var(--space-4)" }}>
          <HandoffButton
            block
            label="Say hi on Telegram"
            onOpen={() => openTelegram("https://t.me/")}
          />
        </div>
      ) : null}
    </Screen>
  );
}

/* ---------------- Edit profile (pushed) ---------------- */

export function EditProfile() {
  const { auth, matching, media } = useServices();
  const { pop } = useNav();
  const user = useAsync(() => auth.getCurrentUser(), []);
  const vibe = useAsync(() => matching.getVibeProfile(), []);

  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [showCourses, setShowCourses] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user.status === "ready") {
      setName(user.data.displayName);
      setBio(user.data.vibeAnalysisText ?? "");
    }
  }, [user.status, user.data]);

  useEffect(() => {
    void cloud.get("show_courses_publicly").then((v) => {
      if (v !== null) setShowCourses(v === "1");
    });
  }, []);

  const filePicker = useRef<HTMLInputElement>(null);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    await auth.updateProfile({ displayName: name.trim(), vibeAnalysisText: bio.trim() });
    await cloud.set("show_courses_publicly", showCourses ? "1" : "0");
    haptic.success();
    pop();
  };

  // Save is MainButton work inside Telegram; the inline button below only
  // exists for browser development, where there is no MainButton.
  useMainButton(isHosted ? { text: "Save changes" } : null, () => void save());

  return (
    <Screen>
      <div className="detail-head">
        <h1 className="t-title">Edit profile</h1>
      </div>

      <button type="button" className="add-course" onClick={() => filePicker.current?.click()}>
        <i className="fa-solid fa-camera" aria-hidden="true" /> Change photo — file upload
      </button>
      <input
        ref={filePicker}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            void media.uploadImage(file).then(() => haptic.success());
          }
        }}
      />

      <label className="form-label" htmlFor="pf-name">
        Preferred name
      </label>
      <input
        id="pf-name"
        className="field"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <label className="form-label" htmlFor="pf-bio">
        Bio
      </label>
      <textarea
        id="pf-bio"
        className="field"
        rows={3}
        value={bio}
        onChange={(e) => setBio(e.target.value)}
      />

      <label className="form-label">Interests</label>
      <div className="card__row">
        {(vibe.data?.interests ?? []).map((tag) => (
          <Pill key={tag} tone="state">
            {tag}
          </Pill>
        ))}
      </div>

      <div className="settings-row" style={{ marginTop: "var(--space-4)" }}>
        <span className="hub-row__label">Show my courses publicly</span>
        <Toggle on={showCourses} onChange={setShowCourses} />
      </div>

      {!isHosted ? (
        <div style={{ marginTop: "var(--space-4)" }}>
          <Button block disabled={saving} onClick={() => void save()}>
            Save changes
          </Button>
        </div>
      ) : null}
    </Screen>
  );
}

function Toggle({ on, onChange }: { on: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      className="toggle"
      role="switch"
      aria-checked={on}
      data-on={on}
      onClick={() => {
        haptic.select();
        onChange(!on);
      }}
    >
      <span className="toggle__knob" />
    </button>
  );
}

/* ---------------- Settings (pushed) ---------------- */

const SETTING_ROWS: { group: string; rows: { key: string; label: string; fallback: boolean }[] }[] =
  [
    {
      group: "Notifications",
      rows: [
        { key: "notify_deadlines", label: "Deadline reminders", fallback: true },
        { key: "notify_matches", label: "Match updates", fallback: true },
        { key: "notify_digest", label: "Daily digest", fallback: false },
      ],
    },
    {
      group: "Privacy",
      rows: [
        { key: "privacy_discoverable", label: "Discoverable by dorm peers", fallback: true },
        { key: "privacy_froshbot_grades", label: "Froshbot uses my grades", fallback: true },
      ],
    },
  ];

export function SettingsScreen() {
  const [values, setValues] = useState<Record<string, boolean>>({});

  useEffect(() => {
    for (const group of SETTING_ROWS) {
      for (const row of group.rows) {
        void cloud.get(row.key).then((v) => {
          setValues((vals) => ({ ...vals, [row.key]: v === null ? row.fallback : v === "1" }));
        });
      }
    }
  }, []);

  const toggle = (key: string, next: boolean) => {
    setValues((vals) => ({ ...vals, [key]: next }));
    // CloudStorage is per-user and survives reinstalls — the right home for
    // preferences on a platform where localStorage dies with the webview.
    void cloud.set(key, next ? "1" : "0");
  };

  return (
    <Screen>
      <div className="detail-head">
        <h1 className="t-title">Settings & privacy</h1>
      </div>

      {SETTING_ROWS.map((group) => (
        <div key={group.group}>
          <div className="section-header">
            <span className="t-micro" style={{ color: "var(--text-hint)" }}>
              {group.group}
            </span>
          </div>
          <Card compact>
            {group.rows.map((row, i) => (
              <div key={row.key} className={`settings-row${i > 0 ? " settings-row--divided" : ""}`}>
                <span className="hub-row__label">{row.label}</span>
                <Toggle
                  on={values[row.key] ?? row.fallback}
                  onChange={(next) => toggle(row.key, next)}
                />
              </div>
            ))}
          </Card>
        </div>
      ))}

      <div className="section-header">
        <span className="t-micro" style={{ color: "var(--text-hint)" }}>
          App
        </span>
      </div>
      <Card compact>
        <button
          type="button"
          className="settings-row settings-row--tappable"
          onClick={() => {
            if (!addToHomeScreen()) {
              showAlert("Adding to the home screen needs a newer Telegram version.");
            }
          }}
        >
          <span className="hub-row__label">Add to home screen</span>
          <i className="fa-solid fa-chevron-right course-row__chevron" aria-hidden="true" />
        </button>
        {/* The theme row states the strategy: follows Telegram, no override. */}
        <div className="settings-row settings-row--divided">
          <span className="hub-row__label">Theme</span>
          <span className="course-row__meta">Follows Telegram</span>
        </div>
      </Card>
    </Screen>
  );
}

/* ---------------- Marketplace (pushed) ---------------- */

/**
 * Student-to-student listings, as the mock (and the iOS app) actually model it.
 * The comp reimagined this screen as a perks-redemption catalogue; the seed has
 * no perks inventory, and the rule is mocks-as-truth — so listings it is, with
 * the points balance carried in the header.
 */
export function MarketplaceScreen() {
  const { marketplace, reward } = useServices();
  const listings = useAsync(() => marketplace.getListings(), []);
  const balance = useAsync(() => reward.getBalance(), []);

  return (
    <Screen>
      <div
        className="detail-head"
        style={{ flexDirection: "row", alignItems: "baseline", gap: 10 }}
      >
        <h1 className="t-title">Marketplace</h1>
        <span className="section-header__spacer" />
        <Pill tone="qualify">{balance.data ? `${count(balance.data.balance)} pts` : "—"}</Pill>
      </div>

      {listings.status === "loading" ? (
        <FeedSkeleton count={2} />
      ) : listings.status === "error" ? (
        <ErrorState onRetry={listings.reload} />
      ) : listings.data.length === 0 ? (
        <EmptyState title="Nothing listed yet" body="Campus listings from your class land here." />
      ) : (
        <div className="stack">
          {listings.data.map((item) => (
            <Card key={item.id} compact>
              <div className="card__body">
                <div className="club-head">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="course-row__name">{item.title}</div>
                    <div className="course-row__meta">
                      {item.category} · {item.seller.displayName}
                    </div>
                  </div>
                  <span className="listing-price">{money(item.price)}</span>
                </div>
                <div className="card__content" style={{ fontSize: "var(--text-caption-size)" }}>
                  {item.description}
                </div>
                <HandoffButton
                  block
                  label="Message the seller"
                  onOpen={() => openTelegram("https://t.me/")}
                />
              </div>
            </Card>
          ))}
        </div>
      )}
    </Screen>
  );
}

/* ---------------- Rewards history (pushed) ---------------- */

export function RewardsHistoryScreen() {
  const { reward } = useServices();
  const history = useAsync(() => reward.getHistory(), []);

  return (
    <Screen>
      <div className="detail-head">
        <h1 className="t-title">Rewards history</h1>
      </div>

      {history.status === "loading" ? (
        <Skeleton height={200} radius={16} />
      ) : history.status === "error" ? (
        <ErrorState onRetry={history.reload} />
      ) : (
        <Card compact>
          {history.data.map((row, i) => (
            <div key={row.id} className={`settings-row${i > 0 ? " settings-row--divided" : ""}`}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="course-row__name">{row.reason}</div>
                <div className="course-row__meta">{relativeShort(row.earnedAt)}</div>
              </div>
              {/* Ledger uses ± pts only — no currency signs near points, ever. */}
              <span className="ledger-amount">+{row.amount}</span>
            </div>
          ))}
        </Card>
      )}
    </Screen>
  );
}

/* ---------------- Horoscope (pushed) ---------------- */

export function HoroscopeScreen() {
  const { auth, horoscope } = useServices();
  const user = useAsync(() => auth.getCurrentUser(), []);
  const sign = user.data?.zodiacSign;
  const reading = useAsync(async () => (sign ? horoscope.getDailyHoroscope(sign) : null), [sign]);

  return (
    <Screen>
      <div className="detail-head">
        <h1 className="t-title">Daily horoscope</h1>
      </div>

      {reading.data ? (
        <Card>
          <div className="card__body">
            <div className="horoscope-head">
              <span className="t-micro" style={{ color: "var(--text-hint)" }}>
                {zodiacSymbol(reading.data.sign)} {reading.data.sign} ·{" "}
                {absoluteDay(reading.data.date)}
              </span>
              <Pill tone="qualify">{reading.data.mood}</Pill>
            </div>
            <div className="card__title">{reading.data.headline}</div>
            <div className="card__content">{reading.data.body}</div>
            <div className="card__row" style={{ marginTop: "var(--space-2)" }}>
              <Pill tone="meta">Lucky number · {reading.data.luckyNumber}</Pill>
              <Pill tone="meta">
                Compatible · {zodiacSymbol(reading.data.compatibleSign)}{" "}
                {reading.data.compatibleSign}
              </Pill>
            </div>
            <button
              type="button"
              className="btn btn--quick"
              onClick={() => {
                const ok = shareToStory(
                  `${window.location.origin}/brand/wordmark.png`,
                  reading.data ? `${reading.data.headline} · Freshman` : "Freshman",
                );
                if (!ok) showAlert("Story sharing needs a newer Telegram version.");
              }}
            >
              <i className="fa-solid fa-share-nodes" aria-hidden="true" /> Share to story
            </button>
            {/* Wordmark footer so screenshots self-attribute. */}
            <img className="horoscope-wordmark" src="/brand/wordmark.png" alt="Freshman" />
          </div>
        </Card>
      ) : (
        <Skeleton height={220} radius={20} />
      )}
    </Screen>
  );
}
